import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { createCloud, updateCloudCredentials } from "../api/clouds";
import CloudSetupGuide from "./CloudSetupGuide";

const PROVIDERS = [
  { value: "m365", label: "Microsoft 365 (Graph)" },
  { value: "azure", label: "Azure Resource Manager" },
  { value: "aws", label: "Amazon Web Services (AWS)" },
];

export default function CloudConnectDialog({ open, onClose, onSuccess, defaultProvider = "", existingCloud = null }) {
  const [provider, setProvider] = useState(defaultProvider);
  
  // Azure/M365 fields
  const [tenantId, setTenantId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  
  // AWS fields
  const [accessKeyId, setAccessKeyId] = useState("");
  const [secretAccessKey, setSecretAccessKey] = useState("");
  const [awsRegion, setAwsRegion] = useState("us-east-1");
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset provider when defaultProvider changes
  React.useEffect(() => {
    setProvider(existingCloud?.provider || defaultProvider);
    setTenantId(existingCloud?.tenantId || "");
    setClientId(existingCloud?.clientId || "");
    setClientSecret("");
    setAccessKeyId(existingCloud?.accessKeyId || "");
    setSecretAccessKey("");
    setAwsRegion(existingCloud?.awsRegion || "us-east-1");
  }, [defaultProvider, existingCloud]);

  function resetForm() {
    setProvider(defaultProvider || "");
    setTenantId("");
    setClientId("");
    setClientSecret("");
    setAccessKeyId("");
    setSecretAccessKey("");
    setAwsRegion("us-east-1");
    setError(null);
    setLoading(false);
  }

  async function handleSubmit(e) {
    e?.preventDefault();
    setError(null);

    if (provider === 'aws') {
      // AWS validation
      if (!accessKeyId || !secretAccessKey) {
        setError("Please fill AWS Access Key ID and Secret Access Key.");
        return;
      }

      if (accessKeyId.trim().length < 16) {
        setError("AWS Access Key ID appears invalid. It should be at least 16 characters.");
        return;
      }

      if (secretAccessKey.trim().length < 20) {
        setError("AWS Secret Access Key appears invalid. It should be at least 20 characters.");
        return;
      }
    } else {
      // Azure/M365 validation
      if (!tenantId || !clientId || !clientSecret) {
        setError("Please fill Tenant ID, Client ID and Client Secret.");
        return;
      }

      // Validate GUID format for Tenant ID and Client ID
      const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      
      if (!guidRegex.test(tenantId.trim())) {
        setError("Invalid Tenant ID format. Please enter a valid GUID (e.g., 12345678-1234-1234-1234-123456789abc). Note: Tenant ID cannot be an email address.");
        return;
      }

      if (!guidRegex.test(clientId.trim())) {
        setError("Invalid Client ID format. Please enter a valid GUID (e.g., 12345678-1234-1234-1234-123456789abc).");
        return;
      }

      if (clientSecret.trim().length < 10) {
        setError("Client Secret appears too short. Please verify you copied the complete secret value.");
        return;
      }
    }

    setLoading(true);
    try {
      const payload = { provider };

      if (provider === 'aws') {
        payload.accessKeyId = accessKeyId.trim();
        payload.secretAccessKey = secretAccessKey.trim();
        payload.awsRegion = awsRegion;
      } else {
        payload.tenantId = tenantId.trim();
        payload.clientId = clientId.trim();
        payload.clientSecret = clientSecret.trim();
      }

      const body = existingCloud
        ? await updateCloudCredentials(existingCloud.id, payload)
        : await createCloud(payload);
      // `body` expected: { id, summary }
      onSuccess && onSuccess(body);
      resetForm();
      onClose && onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.message || 
        err?.response?.data?.detail || 
        err?.response?.data?.error || 
        err?.message || 
        "Connection failed";
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onClose={() => { if (!loading) { resetForm(); onClose && onClose(); } }} maxWidth="sm" fullWidth>
      <DialogTitle>
        {!provider ? 'Choose Cloud Service' : `${existingCloud ? 'Update' : 'Connect'} ${provider === 'aws' ? 'AWS' : provider === 'azure' ? 'Azure' : 'Microsoft 365'}`}
      </DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2 }}>
          {provider && (
            <Accordion variant="outlined" disableGutters sx={{ mt: 1, borderRadius: "8px !important", overflow: "hidden" }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" fontWeight={800}>How to set up {provider === "aws" ? "AWS" : provider === "azure" ? "Azure" : "Microsoft 365"} access</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ borderTop: "1px solid #e2e8f0" }}>
                <CloudSetupGuide provider={provider} />
              </AccordionDetails>
            </Accordion>
          )}
          {!provider ? (
            <>
              <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                Select which cloud service you want to connect to your enterprise dashboard.
              </Alert>
              
              <TextField
                select
                label="Cloud Provider"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                fullWidth
                helperText="Choose your cloud service provider"
                autoFocus
              >
                {PROVIDERS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
            </>
          ) : provider === 'aws' ? (
            <>
              <Alert severity="info" sx={{ mt: 1 }}>
                This connector uses a dedicated IAM user's access key. Grant s3:ListAllMyBuckets to validate it; add read permissions for each report you want. Do not use root keys. The public AWS assessment uses a separate role-based connection.
              </Alert>
              
              <TextField
                label="AWS Access Key ID"
                value={accessKeyId}
                onChange={(e) => setAccessKeyId(e.target.value)}
                fullWidth
                size="small"
                placeholder="AKIAIOSFODNN7EXAMPLE"
                helperText="Your AWS Access Key ID (starts with AKIA)"
              />

              <TextField
                label="AWS Secret Access Key"
                value={secretAccessKey}
                onChange={(e) => setSecretAccessKey(e.target.value)}
                fullWidth
                size="small"
                placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                type="password"
                helperText="Your AWS Secret Access Key"
              />

              <TextField
                select
                label="Primary AWS Region"
                value={awsRegion}
                onChange={(e) => setAwsRegion(e.target.value)}
                fullWidth
                size="small"
                helperText="Select your primary AWS region for monitoring"
              >
                <MenuItem value="us-east-1">US East (N. Virginia)</MenuItem>
                <MenuItem value="us-east-2">US East (Ohio)</MenuItem>
                <MenuItem value="us-west-1">US West (N. California)</MenuItem>
                <MenuItem value="us-west-2">US West (Oregon)</MenuItem>
                <MenuItem value="eu-west-1">EU (Ireland)</MenuItem>
                <MenuItem value="eu-west-2">EU (London)</MenuItem>
                <MenuItem value="eu-central-1">EU (Frankfurt)</MenuItem>
                <MenuItem value="ap-south-1">Asia Pacific (Mumbai)</MenuItem>
                <MenuItem value="ap-southeast-1">Asia Pacific (Singapore)</MenuItem>
                <MenuItem value="ap-southeast-2">Asia Pacific (Sydney)</MenuItem>
                <MenuItem value="ap-northeast-1">Asia Pacific (Tokyo)</MenuItem>
              </TextField>

              <Typography variant="body2" color="text.secondary">
                We will validate the key and fetch the services permitted by its IAM policy. Data is refreshed when you select Refresh.
              </Typography>
            </>
          ) : (
            <>
              <TextField
                label="Tenant ID"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                fullWidth
                size="small"
                placeholder="12345678-1234-1234-1234-123456789abc"
                helperText="Azure AD Tenant ID (must be a GUID, not an email address)"
                error={tenantId && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(tenantId.trim())}
              />

              <TextField
                label="Client ID (Application ID)"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                fullWidth
                size="small"
                placeholder="a0b1c2d3-e4f5-6789-0123-456789abcdef"
                helperText="Application (client) ID from Azure AD app registration"
                error={clientId && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(clientId.trim())}
              />

              <TextField
                label="Client Secret"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                fullWidth
                size="small"
                placeholder="Paste the client secret value"
                type="password"
                helperText="Client secret value (not the secret ID)"
              />

              <Alert severity="info">
                {provider === 'm365'
                  ? 'Required: Microsoft Graph application permissions Organization.Read.All, LicenseAssignment.Read.All, and User.Read.All with admin consent.'
                  : 'Required: assign this app Reader on at least one Azure subscription. A tenant ID alone does not grant subscription access.'}
              </Alert>
            </>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => { if (!loading) { resetForm(); onClose && onClose(); } }} disabled={loading}>
          Cancel
        </Button>

        {!provider ? (
          <Button
            variant="contained"
            disabled={true}
            sx={{ minWidth: 120 }}
          >
            Select Provider
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ minWidth: 120 }}
          >
            {loading ? (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} color="inherit" /> Connecting…
              </Box>
            ) : (
              existingCloud ? "Update connection" : "Connect"
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
